/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HtmlCssDataBindingsPanel — repeatable div-id → public-table bindings for
 * html_css_workspace blocks. Shared by HtmlCssWorkspaceModal (full-screen)
 * and HtmlCssEditor (compact sidebar). One table-list query is shared across
 * all rows; each row only needs a table_slug, not the full field-mapping UI
 * that DataBindingPanel.js provides for single-binding section types.
 *
 * A binding also carries an optional row FILTER. It belongs here rather than
 * with the card settings because the row limit is applied by the server:
 * hiding rows after the fetch would ask for three and render fewer. Filtering
 * in the query keeps "the newest three published events" one question.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHelper } from '../../hooks/usePages';
import { parseAllowedOptions, OPERATORS } from './HtmlCssCardsPanel';

const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 };
const inputStyle = {
  width: '100%',
  padding: '5px 8px',
  fontSize: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 5,
  outline: 'none',
};

function BindingRow({ binding, tables, onChange, onRemove }) {
  const table = tables.find((t) => (t.slug || t.name) === binding.table_slug);
  const filter = binding.filter || { field: '', op: 'eq', values: [] };
  const optionFields = (table?.columns || [])
    .map((c) => ({ ...c, options: parseAllowedOptions(c.allowed_options) }))
    .filter((c) => c.options.length > 0);
  const filterField = optionFields.find((f) => f.name === filter.field);
  const operator = OPERATORS.find((o) => o.key === filter.op) || OPERATORS[1];

  const patchFilter = (patch) => {
    const next = { ...filter, ...patch };
    // A filter with no field is no filter — drop it rather than storing an
    // empty shell the client would have to know to ignore.
    onChange({ filter: next.field ? next : undefined });
  };

  const changeOperator = (key) => {
    const next = OPERATORS.find((o) => o.key === key);
    const values = next.multi ? (filter.values || []) : (filter.values || []).slice(0, 1);
    patchFilter({ op: key, values });
  };

  const toggleValue = (value) => {
    const current = filter.values || [];
    const at = current.indexOf(value);
    patchFilter({ values: at === -1 ? [...current, value] : current.filter((v) => v !== value) });
  };

  return (
    <div
      style={{
        padding: 10,
        marginBottom: 8,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
      <div style={{ flex: 1.2 }}>
        <div style={labelStyle}>Div ID</div>
        <input
          type="text"
          style={inputStyle}
          value={binding.div_id || ''}
          placeholder="e.g. recent-posts"
          onChange={(e) => onChange({ div_id: e.target.value })}
        />
      </div>
      <div style={{ flex: 1.4 }}>
        <div style={labelStyle}>Table</div>
        <select
          style={inputStyle}
          value={binding.table_slug || ''}
          onChange={(e) => onChange({ table_slug: e.target.value })}
        >
          <option value="">— Select table —</option>
          {tables.map((t) => (
            <option key={t.id} value={t.slug || t.name}>
              {t.display_name || t.name}
            </option>
          ))}
        </select>
      </div>
      <div style={{ flex: 0.6 }}>
        <div style={labelStyle}>Limit</div>
        <input
          type="number"
          min={1}
          max={500}
          style={inputStyle}
          value={binding.limit || ''}
          placeholder="10"
          onChange={(e) => onChange({ limit: e.target.value ? parseInt(e.target.value, 10) : undefined })}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div style={labelStyle}>Sort</div>
        <input
          type="text"
          style={inputStyle}
          value={binding.sort || ''}
          placeholder="-created_at"
          onChange={(e) => onChange({ sort: e.target.value || undefined })}
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        title="Remove binding"
        style={{
          flexShrink: 0,
          width: 26,
          height: 26,
          border: '1px solid #fecaca',
          borderRadius: 5,
          background: '#fef2f2',
          color: '#dc2626',
          cursor: 'pointer',
          fontSize: 13,
        }}
      >
        ✕
      </button>
      </div>

      {optionFields.length > 0 ? (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #eef2f7' }}>
          <div style={labelStyle}>Only include rows where</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <select
              style={{ ...inputStyle, flex: 1.2 }}
              aria-label={`Filter field for ${binding.div_id || binding.table_slug}`}
              value={filter.field || ''}
              onChange={(e) => patchFilter({ field: e.target.value, values: [] })}
            >
              <option value="">every row</option>
              {optionFields.map((f) => (
                <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
              ))}
            </select>
            {filter.field ? (
              <select
                style={{ ...inputStyle, flex: 1 }}
                aria-label={`Filter comparison for ${binding.div_id || binding.table_slug}`}
                value={filter.op || 'eq'}
                onChange={(e) => changeOperator(e.target.value)}
              >
                {OPERATORS.filter((o) => o.needsValue).map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            ) : null}
          </div>

          {filter.field ? (
            <div style={{ marginTop: 8 }}>
              {operator.multi ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(filterField?.options || []).map((v) => {
                    const on = (filter.values || []).indexOf(v) !== -1;
                    return (
                      <button
                        key={v}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleValue(v)}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
                          cursor: 'pointer',
                          border: `1px solid ${on ? 'var(--primary-color, #4e2e80)' : '#e2e8f0'}`,
                          background: on ? 'var(--primary-color, #4e2e80)' : '#fff',
                          color: on ? '#fff' : '#64748b',
                        }}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <select
                  style={inputStyle}
                  aria-label={`Filter value for ${binding.div_id || binding.table_slug}`}
                  value={(filter.values || [])[0] || ''}
                  onChange={(e) => patchFilter({ values: [e.target.value] })}
                >
                  <option value="">choose a value</option>
                  {(filterField?.options || []).map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              )}
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0', lineHeight: 1.5 }}>
                Applied in the query, so the limit above counts only matching rows.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function HtmlCssDataBindingsPanel({ appId, bindings, onChange }) {
  const rows = bindings || [];

  const { data: tablesData } = useQuery({
    queryKey: ['tableList', appId, 'withFields'],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200&include_fields=1`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const tables = (tablesData?.tables || tablesData?.results || []).filter((t) => t.visibility === 'public');

  const updateRow = (index, patch) => {
    onChange(rows.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...rows, { div_id: '', table_slug: '' }]);
  };

  return (
    <div style={{ padding: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 10 }}>
        DATA BINDINGS
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        Bind a public table's records to a <code>div id</code> in your HTML. Your JS can then read{' '}
        <code>window.ARCEE_DATA['your-div-id']</code>.
      </div>

      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>No bindings yet.</div>
      )}

      {rows.map((binding, index) => (
        <BindingRow
          key={index}
          binding={binding}
          tables={tables}
          onChange={(patch) => updateRow(index, patch)}
          onRemove={() => removeRow(index)}
        />
      ))}

      <button
        type="button"
        onClick={addRow}
        style={{
          padding: '6px 14px',
          fontSize: 12,
          fontWeight: 600,
          border: '1px dashed #cbd5e1',
          borderRadius: 6,
          background: 'transparent',
          color: '#475569',
          cursor: 'pointer',
        }}
      >
        + Add binding
      </button>
    </div>
  );
}
